import React, { useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FileText, Image as ImageIcon, RefreshCw, Copy, Check, Volume2, Loader2 } from 'lucide-react';
import { Logo, apiHeaders, ttsSpeak } from './chatUtils';

function CodeBlock({ inline, children, ...props }) {
  const [copied, setCopied] = useState(false);

  if (inline) {
    return (
      <code className="bg-overlay text-[#e2e8f0] px-1.5 py-0.5 rounded text-[13px] font-mono" {...props}>
        {children}
      </code>
    );
  }

  const codeText = String(children).replace(/\n$/, '');
  const copyCode = () => {
    navigator.clipboard.writeText(codeText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative group/code my-4 -mx-1">
      <div className="flex items-center justify-between px-4 py-2 bg-app border border-line border-b-0 rounded-t-xl">
        <span className="text-[11px] text-content-3 font-mono">code</span>
        <button onClick={copyCode}
          className="text-[11px] flex items-center gap-1 text-content-3 hover:text-content-2 transition-colors font-mono"
          data-testid="copy-code-btn">
          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="bg-app border border-line border-t-0 rounded-b-xl p-4 overflow-x-auto">
        <code className="text-[13px] font-mono text-content-2 leading-relaxed" {...props}>{children}</code>
      </pre>
    </div>
  );
}

export default function MessageBubble({ message, isStreaming, onRegenerate }) {
  const isUser = message.role === 'user';
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleSpeak = useCallback(async () => {
    if (isSpeaking) return;
    setIsSpeaking(true);
    const headers = apiHeaders();
    delete headers['Content-Type'];
    const audio = await ttsSpeak(message.content, headers);
    if (audio) {
      audio.onended = () => setIsSpeaking(false);
      audio.onerror = () => setIsSpeaking(false);
    } else {
      setIsSpeaking(false);
    }
  }, [message.content, isSpeaking]);

  const handleRegenerate = async () => {
    if (isRegenerating || !onRegenerate) return;
    setIsRegenerating(true);
    await onRegenerate(message.id);
    setIsRegenerating(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(message.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // User message - right-aligned, minimal bubble
  if (isUser) {
    return (
      <div className="flex justify-end" data-testid="message-user">
        <div className="max-w-[80%] md:max-w-[70%]">
          {message.file_name && (
            <div className="flex justify-end mb-1.5">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[12px] bg-overlay text-content-3 border border-line">
                {message.file_type?.startsWith('image/') ? <ImageIcon className="w-3 h-3" strokeWidth={1.5} /> : <FileText className="w-3 h-3" strokeWidth={1.5} />}
                <span className="truncate max-w-[120px] font-sora">{message.file_name}</span>
              </span>
            </div>
          )}
          <div className="bg-surface-2 rounded-2xl rounded-br-lg px-4 py-3 text-[14px] leading-relaxed text-content font-sora">
            {message.content}
          </div>
        </div>
      </div>
    );
  }

  // Assistant message - left-aligned, full width, clean layout
  return (
    <div className="flex gap-3 items-start group/msg" data-testid="message-assistant">
      <div className="w-7 h-7 rounded-lg bg-surface border border-line flex items-center justify-center flex-shrink-0 mt-1">
        <Logo size="sm" className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[14px] leading-[1.8] font-sora text-content [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
          <ReactMarkdown remarkPlugins={[remarkGfm]}
            components={{
              code({ inline, children, ...props }) {
                return <CodeBlock inline={inline} {...props}>{children}</CodeBlock>;
              },
              p({ children }) { return <p className="mb-3">{children}</p>; },
              ul({ children }) { return <ul className="list-disc list-outside ml-5 mb-3 space-y-1 text-content-2 marker:text-content-3">{children}</ul>; },
              ol({ children }) { return <ol className="list-decimal list-outside ml-5 mb-3 space-y-1 text-content-2">{children}</ol>; },
              li({ children }) { return <li className="text-content-2 pl-1">{children}</li>; },
              h1({ children }) { return <h1 className="text-lg font-heading font-bold mb-3 mt-5 text-content tracking-tight">{children}</h1>; },
              h2({ children }) { return <h2 className="text-base font-heading font-bold mb-2 mt-4 text-content tracking-tight">{children}</h2>; },
              h3({ children }) { return <h3 className="text-sm font-heading font-semibold mb-2 mt-3 text-content">{children}</h3>; },
              strong({ children }) { return <strong className="text-content font-semibold">{children}</strong>; },
              em({ children }) { return <em className="text-content-2 italic">{children}</em>; },
              a({ children, href }) { return <a href={href} className="text-emerald-500 hover:underline underline-offset-2" target="_blank" rel="noreferrer">{children}</a>; },
              blockquote({ children }) {
                return <blockquote className="border-l-2 border-line-2 pl-4 my-3 text-content-2">{children}</blockquote>;
              },
              table({ children }) {
                return <div className="overflow-x-auto my-4 rounded-xl border border-line"><table className="w-full text-[13px]">{children}</table></div>;
              },
              th({ children }) { return <th className="bg-overlay px-3 py-2 text-left text-content-2 font-medium border-b border-line text-[12px]">{children}</th>; },
              td({ children }) { return <td className="px-3 py-2 text-content-2 border-b border-white/[0.03] text-[13px]">{children}</td>; },
              hr() { return <hr className="border-line my-5" />; },
            }}>
            {message.content}
          </ReactMarkdown>
          {isStreaming && <span className="inline-block w-0.5 h-4 bg-zinc-400 ml-0.5 animate-pulse rounded-full align-text-bottom" />}
        </div>

        {/* Hover action buttons */}
        {!isStreaming && message.id && (
          <div className="flex items-center gap-0.5 mt-1.5 opacity-0 group-hover/msg:opacity-100 transition-opacity duration-150">
            <button onClick={handleCopy} className="p-1.5 rounded-lg text-content-3 hover:bg-overlay-hover hover:text-content-2 transition-colors" data-testid="copy-msg-btn" title="Copy">
              {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" strokeWidth={1.5} />}
            </button>
            <button onClick={handleSpeak} disabled={isSpeaking} className="p-1.5 rounded-lg text-content-3 hover:bg-overlay-hover hover:text-content-2 transition-colors" data-testid="tts-btn" title="Listen">
              {isSpeaking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Volume2 className="w-3.5 h-3.5" strokeWidth={1.5} />}
            </button>
            {onRegenerate && (
              <button onClick={handleRegenerate} disabled={isRegenerating} className="p-1.5 rounded-lg text-content-3 hover:bg-overlay-hover hover:text-content-2 transition-colors" data-testid="regenerate-btn" title="Regenerate">
                {isRegenerating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5} />}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
